import { FormEvent, useEffect, useState } from 'react';
import type { DiscountCode, DiscountType } from '@lr/shared';
import { apiFetch } from '../../lib/api';

export function AdminDiscountCodes() {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<DiscountCode | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    apiFetch<DiscountCode[]>('/api/admin/discount-codes')
      .then(setCodes)
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function toggleActive(c: DiscountCode) {
    setError(null);
    try {
      await apiFetch(`/api/admin/discount-codes/${c.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !c.is_active })
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function deleteCode(c: DiscountCode) {
    if (
      !window.confirm(
        `Delete code ${c.code}? Codes that have been redeemed cannot be deleted (disable them instead).`
      )
    )
      return;
    setError(null);
    try {
      await apiFetch(`/api/admin/discount-codes/${c.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Discount codes</h1>
          <div className="sub">{codes.length} {codes.length === 1 ? 'code' : 'codes'}.</div>
        </div>
        <div className="actions">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New code
          </button>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="a-empty">
            <div className="h">No discount codes</div>
            <div className="p">Codes give specific customers a price break at checkout.</div>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New code
            </button>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th style={{ width: 90 }}>Uses</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 130 }}>Valid from</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id}>
                    <td className="id" style={{ fontWeight: 600, color: 'var(--a-ink)' }}>
                      {c.code}
                    </td>
                    <td>
                      <span className="badge badge-neutral">{c.discount_type.replace(/_/g, ' ')}</span>
                    </td>
                    <td>{formatValue(c)}</td>
                    <td className="num">
                      {c.uses_count}
                      {c.max_uses ? ` / ${c.max_uses}` : ''}
                    </td>
                    <td>
                      {c.is_active ? (
                        <span className="badge badge-good">Active</span>
                      ) : (
                        <span className="badge badge-neutral">Disabled</span>
                      )}
                    </td>
                    <td>{new Date(c.valid_from).toLocaleDateString('en-GB')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => setEditing(c)}
                          style={{ fontSize: 12 }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => void toggleActive(c)}
                          style={{ fontSize: 12 }}
                        >
                          {c.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => void deleteCode(c)}
                          style={{ fontSize: 12, color: 'var(--a-danger)' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          onClose={(created) => {
            setShowCreate(false);
            if (created) load();
          }}
        />
      )}
      {editing && (
        <EditModal
          code={editing}
          onClose={(saved) => {
            setEditing(null);
            if (saved) load();
          }}
        />
      )}
    </>
  );
}

function formatValue(c: DiscountCode): string {
  if (c.discount_type === 'free_delivery') return 'Free delivery';
  if (c.discount_type === 'percent_off_bundle') return `${c.discount_value}% off bundle`;
  return `£${(c.discount_value / 100).toFixed(2)} off`;
}

function EditModal({
  code,
  onClose
}: {
  code: DiscountCode;
  onClose: (saved?: boolean) => void;
}) {
  // discount_value is stored as pence for fixed_off_order, as % for
  // percent_off_bundle. UI works in pounds for the £ type — convert on
  // load/save so the admin never has to think in pence.
  const isFixed = code.discount_type === 'fixed_off_order';
  const [value, setValue] = useState(
    isFixed ? (code.discount_value / 100).toFixed(2) : String(code.discount_value)
  );
  const [description, setDescription] = useState(code.description ?? '');
  const [maxUses, setMaxUses] = useState(
    code.max_uses != null ? String(code.max_uses) : ''
  );
  const [isActive, setIsActive] = useState(code.is_active);
  const [validUntil, setValidUntil] = useState(
    code.valid_until ? code.valid_until.slice(0, 10) : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const numericValue = Number(value) || 0;
      await apiFetch(`/api/admin/discount-codes/${code.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          // For £-off codes the field is in pounds in the UI; convert
          // to integer pence for the DB.
          discount_value: isFixed
            ? Math.round(numericValue * 100)
            : Math.round(numericValue),
          description: description || undefined,
          max_uses: maxUses ? Number(maxUses) : null,
          is_active: isActive,
          valid_until: validUntil
            ? new Date(validUntil).toISOString()
            : null
        })
      });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="a-modal-overlay open" onClick={() => onClose()} />
      <aside className="a-modal open">
        <form onSubmit={onSubmit} style={{ display: 'contents' }}>
          <div className="a-modal-head">
            <h2>Edit {code.code}</h2>
            <button type="button" className="btn-ghost" onClick={() => onClose()}>
              ×
            </button>
          </div>
          <div className="a-modal-body">
            {error && <div className="a-alert a-alert-error">{error}</div>}
            <div className="a-form">
              <div className="a-field">
                <label>Type</label>
                <input
                  type="text"
                  value={code.discount_type.replace(/_/g, ' ')}
                  disabled
                />
                <div className="help">
                  Code and type can&apos;t be changed after creation — create a new
                  code instead.
                </div>
              </div>
              {code.discount_type !== 'free_delivery' && (
                <div className="a-field">
                  <label>
                    {code.discount_type === 'percent_off_bundle'
                      ? 'Percent (1-100)'
                      : 'Amount off (£)'}
                  </label>
                  <input
                    type="number"
                    step={isFixed ? '0.01' : '1'}
                    min="0"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  {isFixed && (
                    <div className="help">In pounds — e.g. 5 means £5 off.</div>
                  )}
                </div>
              )}
              <div className="a-field">
                <label>Description (internal)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="a-field">
                <label>Max uses</label>
                <input
                  type="number"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
              <div className="a-field">
                <label>Valid until</label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
                <div className="help">Leave blank for no expiry.</div>
              </div>
              <div className="a-field">
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  Active
                </label>
              </div>
            </div>
          </div>
          <div className="a-modal-foot">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => onClose()}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}

function CreateModal({ onClose }: { onClose: (created?: boolean) => void }) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<DiscountType>('free_delivery');
  const [value, setValue] = useState('0');
  const [description, setDescription] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // £-off codes: admin types pounds, server stores pence.
      const numericValue = Number(value) || 0;
      const dbValue =
        type === 'fixed_off_order'
          ? Math.round(numericValue * 100)
          : Math.round(numericValue);
      await apiFetch('/api/admin/discount-codes', {
        method: 'POST',
        body: JSON.stringify({
          code: code.toUpperCase(),
          discount_type: type,
          discount_value: dbValue,
          description: description || undefined,
          max_uses: maxUses ? Number(maxUses) : undefined,
          is_active: true
        })
      });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="a-modal-overlay open" onClick={() => onClose()} />
      <aside className="a-modal open">
        <form onSubmit={onSubmit} style={{ display: 'contents' }}>
          <div className="a-modal-head">
            <h2>New discount code</h2>
            <button type="button" className="btn-ghost" onClick={() => onClose()}>
              ×
            </button>
          </div>
          <div className="a-modal-body">
            {error && <div className="a-alert a-alert-error">{error}</div>}
            <div className="a-form">
              <div className="a-field">
                <label>Code *</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME10"
                  required
                  style={{ textTransform: 'uppercase' }}
                />
                <div className="help">What customers will type at checkout. Auto-uppercased.</div>
              </div>
              <div className="a-field">
                <label>Type *</label>
                <select value={type} onChange={(e) => setType(e.target.value as DiscountType)}>
                  <option value="free_delivery">Free delivery</option>
                  <option value="percent_off_bundle">% off bundle</option>
                  <option value="fixed_off_order">£ off order</option>
                </select>
              </div>
              {type !== 'free_delivery' && (
                <div className="a-field">
                  <label>
                    {type === 'percent_off_bundle'
                      ? 'Percent (1-100)'
                      : 'Amount off (£)'}
                  </label>
                  <input
                    type="number"
                    step={type === 'fixed_off_order' ? '0.01' : '1'}
                    min="0"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={
                      type === 'percent_off_bundle' ? '10' : '5.00'
                    }
                  />
                  {type === 'fixed_off_order' && (
                    <div className="help">In pounds — e.g. 5 means £5 off.</div>
                  )}
                </div>
              )}
              <div className="a-field">
                <label>Description (internal)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="a-field">
                <label>Max uses</label>
                <input
                  type="number"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
            </div>
          </div>
          <div className="a-modal-foot">
            <button type="button" className="btn-secondary" onClick={() => onClose()}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create code'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
